import type { ReactNode } from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  eyebrow?: string
  subtitle?: ReactNode
  footer?: ReactNode
  children?: ReactNode
  className?: string
}

/**
 * Shared SONAR modal — built on Radix Dialog (focus trap, scroll lock, Escape /
 * overlay close, restore focus) and styled with the SONAR palette. Used by every
 * section's drill-downs (Cash Collections, Sales Report, DSD Center).
 *
 * Presentational: the parent owns the `open` boolean and `onClose`.
 */
export function Modal({ open, onClose, title, eyebrow, subtitle, footer, children, className }: ModalProps) {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm dark:bg-black/60" />
        <DialogPrimitive.Content
          aria-describedby={subtitle ? undefined : ""}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2",
            "max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border border-stone-200 bg-white p-6 shadow-xl",
            "focus:outline-none dark:border-stone-800 dark:bg-stone-900",
            className,
          )}
        >
          <div className="mb-4 pr-8">
            {eyebrow ? (
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-champagne-700 dark:text-champagne-300">
                {eyebrow}
              </p>
            ) : null}
            <DialogPrimitive.Title className="mt-1 text-lg font-semibold tracking-tight text-stone-900 dark:text-stone-100">
              {title}
            </DialogPrimitive.Title>
            {subtitle ? (
              <DialogPrimitive.Description className="mt-0.5 text-sm text-stone-500 dark:text-stone-400">
                {subtitle}
              </DialogPrimitive.Description>
            ) : null}
          </div>

          {children}

          {footer ? (
            <p className="mt-5 border-t border-stone-100 pt-3 text-[11px] leading-relaxed text-stone-400 dark:border-stone-800 dark:text-stone-500">
              {footer}
            </p>
          ) : null}

          <DialogPrimitive.Close
            aria-label="Close"
            className="absolute right-4 top-4 rounded-md p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700 dark:hover:bg-stone-800 dark:hover:text-stone-200"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default Modal
